import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Banknote, ArrowLeft, Calendar, MapPin, DollarSign } from 'lucide-react';

const PreOrderCashPage = () => {
  const [searchParams] = useSearchParams();
  const childName = searchParams.get('child') || 'your child';
  const total = searchParams.get('total') || '0';
  const eventSlug = searchParams.get('event');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-8">
      <div className="max-w-xl mx-auto px-4">
        <Card className="border-green-200">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-800">Order Reserved!</CardTitle>
            <CardDescription className="text-base">
              Your order has been reserved - please bring cash payment
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Order Summary */}
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex justify-between items-center">
                <span className="text-stone-600">Order for:</span>
                <span className="font-semibold">{childName}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-stone-600">Amount Due:</span>
                <span className="font-bold text-xl text-green-600">{formatCurrency(parseFloat(total))}</span>
              </div>
            </div>

            {/* Cash Payment Instructions */}
            <div className="space-y-4">
              <h3 className="font-semibold text-stone-800 flex items-center gap-2">
                <Banknote className="h-5 w-5 text-green-600" />
                Cash Payment Instructions
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <Calendar className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">When to Pay</p>
                    <p className="text-sm text-stone-600">Bring cash to the registration table on Picture Day</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <MapPin className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Where to Pay</p>
                    <p className="text-sm text-stone-600">Check in at the Bright Eyes Photography table before your photo session</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <DollarSign className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-800">Exact Change Appreciated</p>
                    <p className="text-sm text-green-700">Please bring exact cash: <span className="font-bold">{formatCurrency(parseFloat(total))}</span></p>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> Your order has been reserved. If you do not pay at registration, your order will be cancelled. Please arrive a few minutes early to allow time for payment before your photo session.
              </p>
            </div>

            {/* What's Next */}
            <div className="space-y-2">
              <h4 className="font-medium text-stone-800">What happens next?</h4>
              <ul className="text-sm text-stone-600 space-y-1 list-disc list-inside">
                <li>Arrive at Picture Day at your scheduled time</li>
                <li>Check in at the registration table with cash payment</li>
                <li>Get your photos taken!</li>
                <li>You'll receive an email with your gallery link once photos are ready</li>
              </ul>
            </div>

            {/* Back Link */}
            <div className="pt-4 border-t">
              <Link to={eventSlug ? `/preorder/${eventSlug}` : '/'}>
                <Button variant="outline" className="w-full">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {eventSlug ? 'Place Another Order' : 'Return Home'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PreOrderCashPage;
